/**
 * billing.ts
 *
 * Pure helpers for plan-based access control. Extracted from
 * `BillingContext` so the logic is testable without React + jsdom
 * setup, and so other surfaces (route guards, conditional UI in
 * pages) can compute the same answer without going through the
 * Context.
 *
 * Source of truth for which modules are bundled with each plan
 * stays in `BillingContext.PLAN_FEATURES` — the value flows in
 * here so the constant doesn't get duplicated.
 */

import type { ModuleKey } from '../types';
import type { PlanFeatures } from '../context/BillingContext';


/**
 * Returns true if a given module is accessible on the supplied
 * `features` snapshot, accounting for per-subscription module
 * overrides (add-ons purchased outside the bundle).
 *
 * Decision tree:
 *   1. `features.modules` contains `key` → true (bundled)
 *   2. `overrides[key] === true` → true (add-on)
 *   3. otherwise → false
 *
 * `overrides[key] === false` is honoured exactly the same way as
 * "not present" — the bundle still wins. This matches the Phase-1
 * behaviour where overrides only ever GRANTED access; revoking a
 * bundled module would need a different mechanism (e.g. reducing
 * the plan).
 */
export function canAccessModule(
  features: PlanFeatures,
  overrides: Record<string, boolean> | null | undefined,
  key: ModuleKey,
): boolean {
  if (features.modules.includes(key)) return true;
  return overrides?.[key] === true;
}
